import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'
import { API_BASE_URL, TOKEN_KEYS } from '../lib/constants'
import type {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  SendVerificationRequest,
  VerifyPhoneRequest,
  User,
  PointBalance,
  PointHistory,
  ChargeRequest,
  ChargeResponse,
  ConfirmChargeRequest,
  ConfirmChargeResponse,
  Payment,
  NotificationLog,
  NotificationStatistics,
  UserSettings,
} from '../types'

class ApiClient {
  private client: AxiosInstance
  private isRefreshing = false
  private refreshSubscribers: ((token: string) => void)[] = []

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor - add auth token
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN)
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor - handle token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiResponse<unknown>>) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            return new Promise((resolve) => {
              this.refreshSubscribers.push((token: string) => {
                originalRequest.headers.Authorization = `Bearer ${token}`
                resolve(this.client(originalRequest))
              })
            })
          }

          originalRequest._retry = true
          this.isRefreshing = true

          try {
            const refreshToken = localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN)
            if (!refreshToken) {
              throw new Error('No refresh token')
            }

            const response = await this.client.post<ApiResponse<{ token: string }>>('/api/auth/refresh', {
              refreshToken,
            })

            if (response.data.success && response.data.data) {
              // Backend returns 'token' instead of 'accessToken'
              const { token } = response.data.data
              localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, token)

              this.refreshSubscribers.forEach((callback) => callback(token))
              this.refreshSubscribers = []

              originalRequest.headers.Authorization = `Bearer ${token}`
              return this.client(originalRequest)
            }
          } catch (refreshError) {
            // Clear tokens and redirect to login
            this.clearTokens()
            window.location.href = '/login'
            return Promise.reject(refreshError)
          } finally {
            this.isRefreshing = false
          }
        }

        return Promise.reject(error)
      }
    )
  }

  private clearTokens() {
    localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN)
    localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN)
    localStorage.removeItem(TOKEN_KEYS.USER)
  }

  setTokens(accessToken: string, refreshToken: string) {
    localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken)
    localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken)
  }

  logout() {
    this.clearTokens()
  }

  // Auth endpoints
  async login(data: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await this.client.post<ApiResponse<LoginResponse>>('/api/auth/login', data)
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Login failed')
      }
      // Backend returns 'token' instead of 'accessToken'
      const { token, refreshToken } = response.data.data as any
      this.setTokens(token, refreshToken)
      return response.data.data
    } catch (error: any) {
      // Extract error message from axios error response
      if (error.response?.data?.error?.message) {
        throw new Error(error.response.data.error.message)
      }
      throw error
    }
  }

  async register(data: RegisterRequest): Promise<RegisterResponse> {
    try {
      const response = await this.client.post<ApiResponse<RegisterResponse>>('/api/auth/register', data)
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Registration failed')
      }
      // Backend returns 'token' instead of 'accessToken'
      const { token, refreshToken } = response.data.data as any
      this.setTokens(token, refreshToken)
      return response.data.data
    } catch (error: any) {
      // Extract error message from axios error response
      if (error.response?.data?.error?.message) {
        throw new Error(error.response.data.error.message)
      }
      throw error
    }
  }

  async sendVerificationCode(data: SendVerificationRequest): Promise<{ expiresIn: number }> {
    const response = await this.client.post<ApiResponse<{ message: string; expiresIn: number }>>('/api/auth/send-verification', data)
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to send verification code')
    }
    return { expiresIn: response.data.data.expiresIn }
  }

  async verifyPhone(data: VerifyPhoneRequest): Promise<boolean> {
    const response = await this.client.post<ApiResponse<{ verified: boolean }>>('/api/auth/verify-phone', data)
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Verification failed')
    }
    return response.data.data.verified
  }

  async getMe(): Promise<User> {
    const response = await this.client.get<ApiResponse<{ user: User }>>('/api/auth/me')
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to get user info')
    }
    return response.data.data.user
  }

  async withdrawAccount(password: string): Promise<void> {
    const response = await this.client.post<ApiResponse<{ message: string }>>('/api/auth/withdraw', { password })
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to withdraw account')
    }
    this.clearTokens()
  }

  // Points endpoints
  async getPointBalance(): Promise<PointBalance> {
    const response = await this.client.get<ApiResponse<{ points: number; currency: string }>>('/api/points/balance')
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to get point balance')
    }
    // The API returns simple balance, we need to adapt
    return {
      total_points: response.data.data.points,
      points_by_plan: [],
    }
  }

  async getPointHistory(limit = 50, offset = 0): Promise<{ history: PointHistory[] }> {
    const response = await this.client.get<ApiResponse<{ history: PointHistory[] }>>('/api/points/history', {
      params: { limit, offset },
    })
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to get point history')
    }
    return response.data.data
  }

  // Billing endpoints
  async requestCharge(data: ChargeRequest): Promise<ChargeResponse> {
    const response = await this.client.post<ApiResponse<ChargeResponse>>('/api/billing/charge/request', data)
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to request charge')
    }
    return response.data.data
  }

  async confirmCharge(data: ConfirmChargeRequest): Promise<ConfirmChargeResponse> {
    const response = await this.client.post<ApiResponse<ConfirmChargeResponse>>('/api/billing/charge/confirm', data)
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to confirm charge')
    }
    return response.data.data
  }

  async getPaymentHistory(limit = 50, offset = 0): Promise<{ payments: Payment[] }> {
    const response = await this.client.get<ApiResponse<{ payments: Payment[] }>>('/api/billing/payments', {
      params: { limit, offset },
    })
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to get payment history')
    }
    return response.data.data
  }

  // Notification endpoints
  async getNotificationHistory(limit = 50, offset = 0): Promise<{ history: NotificationLog[] }> {
    const response = await this.client.get<ApiResponse<{ history: NotificationLog[] }>>('/api/notify/history', {
      params: { limit, offset },
    })
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to get notification history')
    }
    return response.data.data
  }

  async getNotificationStatistics(): Promise<NotificationStatistics> {
    const response = await this.client.get<ApiResponse<NotificationStatistics>>('/api/notify/statistics')
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to get notification statistics')
    }
    return response.data.data
  }

  // Settings endpoints
  async getSettings(): Promise<UserSettings> {
    const response = await this.client.get<ApiResponse<UserSettings>>('/api/settings')
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to get settings')
    }
    return response.data.data
  }

  async updateNotificationType(notification_type: 'alimtalk' | 'alimtalk_with_link'): Promise<void> {
    const response = await this.client.put<ApiResponse<{ message: string }>>('/api/settings/notification', {
      notification_type,
    })
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to update notification type')
    }
  }

  async updateFallback(fallback_enabled: boolean): Promise<void> {
    const response = await this.client.put<ApiResponse<{ message: string }>>('/api/settings/fallback', {
      fallback_enabled,
    })
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to update fallback setting')
    }
  }

  async updatePhone(phone: string): Promise<void> {
    const response = await this.client.put<ApiResponse<{ phone: string; message: string }>>('/api/settings/phone', {
      phone,
    })
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to update phone')
    }
  }

  async updateName(name: string): Promise<void> {
    const response = await this.client.put<ApiResponse<{ name: string; message: string }>>('/api/settings/name', {
      name,
    })
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to update name')
    }
  }
}

export const apiClient = new ApiClient()
